import React, { useState } from 'react';
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Column, WorkItem, DragEndResult } from 'api/todo/types';
import { KanbanColumn } from './KanbanColumn';

interface KanbanBoardProps {
  columns: Column[];
  workItems: WorkItem[];
  columnTotals: Record<number, number>;
  isOwner: boolean;
  onDragEnd: (result: DragEndResult) => void;
  onTaskClick: (task: WorkItem) => void;
  onAddTask: (columnId: number) => void;
  onAddColumn: (name: string) => void;
  onColumnMenu: (columnId: number, action: 'rename' | 'delete', newName?: string) => void;
  onLoadMore: () => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  columns,
  workItems,
  columnTotals,
  isOwner,
  onDragEnd,
  onTaskClick,
  onAddTask,
  onAddColumn,
  onColumnMenu,
  onLoadMore,
}) => {
  const [addColumnDialogOpen, setAddColumnDialogOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');

  const handleAddColumn = () => {
    const trimmedName = newColumnName.trim();
    if (trimmedName) {
      onAddColumn(trimmedName);
      setNewColumnName('');
      setAddColumnDialogOpen(false);
    }
  };

  // Group work items by column
  const tasksByColumn = workItems.reduce((acc, task) => {
    if (!acc[task.column_id]) {
      acc[task.column_id] = [];
    }
    acc[task.column_id].push(task);
    return acc;
  }, {} as Record<number, WorkItem[]>);

  // Sort columns by sort_order
  const sortedColumns = [...columns].sort((a, b) => a.sort_order - b.sort_order);

  // Sort tasks within each column by sort_order
  Object.keys(tasksByColumn).forEach((columnId) => {
    tasksByColumn[Number(columnId)].sort((a, b) => a.sort_order - b.sort_order);
  });

  // Handle task drop
  const handleTaskDrop = (taskId: number, sourceColumnId: number, destColumnId: number, destIndex: number) => {
    onDragEnd({
      type: 'task',
      sourceId: taskId,
      sourceColumnId,
      destinationColumnId: destColumnId,
      destinationIndex: destIndex,
      closestEdge: null,
    });
  };

  // Handle moving column left
  const handleMoveColumnLeft = (columnId: number) => {
    const index = sortedColumns.findIndex((c) => c.column_id === columnId);
    if (index > 0) {
      onDragEnd({
        type: 'column',
        sourceId: columnId,
        destinationIndex: index - 1,
        closestEdge: null,
      });
    }
  };

  // Handle moving column right
  const handleMoveColumnRight = (columnId: number) => {
    const index = sortedColumns.findIndex((c) => c.column_id === columnId);
    if (index < sortedColumns.length - 1) {
      onDragEnd({
        type: 'column',
        sourceId: columnId,
        destinationIndex: index + 1,
        closestEdge: null,
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          overflowX: 'auto',
          p: { xs: 1, sm: 2 },
          pb: { xs: 2, sm: 2 },
        }}
      >
        {sortedColumns.map((column, index) => (
          <KanbanColumn
            key={`column-${column.column_id}`}
            column={column}
            columns={sortedColumns}
            tasks={tasksByColumn[column.column_id] || []}
            columnTotal={columnTotals[column.column_id] || (tasksByColumn[column.column_id] || []).length}
            index={index}
            isOwner={isOwner}
            isFirst={index === 0}
            isLast={index === sortedColumns.length - 1}
            onTaskClick={onTaskClick}
            onAddTask={() => onAddTask(column.column_id)}
            onMenuAction={(action, newName) => onColumnMenu(column.column_id, action, newName)}
            onTaskDrop={handleTaskDrop}
            onMoveLeft={() => handleMoveColumnLeft(column.column_id)}
            onMoveRight={() => handleMoveColumnRight(column.column_id)}
            onLoadMore={onLoadMore}
          />
        ))}
        {isOwner && (
          <Box sx={{ minWidth: { xs: '85vw', sm: 300 }, display: 'flex', alignItems: 'flex-start', pt: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddColumnDialogOpen(true)}
              sx={{
                minHeight: { xs: 120, sm: 200 },
                width: '100%',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Add Column
            </Button>
          </Box>
        )}
      </Box>

      {/* Add Column Dialog */}
      <Dialog open={addColumnDialogOpen} onClose={() => setAddColumnDialogOpen(false)}>
        <DialogTitle>Add Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Column Name"
            fullWidth
            variant="outlined"
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddColumn();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddColumn} variant="contained" disabled={!newColumnName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
